import supabase from '../config/supabase';
import { handleServiceError } from '../utils/errorHandler';
import { StudentPhoto, PhotoPosition } from '../types';
import {
  STUDENT_PHOTOS_BUCKET,
  STUDENT_BODY_PHOTO_POSITIONS,
  buildLegacyStudentBodyPhotoPath,
  buildPortalUserProfilePhotoPath,
  getLatestStudentBodyPhotoAssessmentDate,
  getStudentBodyPhotoFileNamesToResolve,
  getStudentPhotoSignedUrl,
  listLegacyPortalBodyPhotoFileNames,
  listStudentBodyPhotoAssessmentDates,
  listStudentBodyPhotoFileNames,
  listStudentEquipmentPhotos,
  resolveAvailableStudentPhotoUrl,
  resolveStudentBodyPhotoUrl,
  resolveStudentProfilePhotoUrl,
  withPhotoCacheBuster,
  type StudentEquipmentPhoto,
} from './studentPhotoStorage';
import { StudentPhotoUploadService } from './StudentPhotoUploadService';
import { insertStorageFileByPath } from './storageFiles';

export interface BodyPhotoUploadItem {
  position: PhotoPosition;
  file: File;
}

export interface BodyPhotoUploadOptions {
  studentToken?: string;
  /** YYYY-MM-DD — pasta de avaliação no Storage (importação de fotos antigas). */
  assessmentDate?: string;
}

export interface GetBodyPhotosOptions {
  bustCache?: boolean;
  assessmentDate?: string;
}

export interface StudentBodyPhotoAssessment {
  assessmentDate: string;
  photos: StudentPhoto[];
}

export type { StudentEquipmentPhoto };

class PhotoServiceClass {
  private async getBodyPhotosFromClientStorage(
    studentId: number,
    legacyUserId?: string | null,
    options: GetBodyPhotosOptions = {},
  ): Promise<StudentPhoto[]> {
    const assessmentDate = options.assessmentDate
      ?? await getLatestStudentBodyPhotoAssessmentDate(studentId);
    const datedFolderFileNames = assessmentDate
      ? await listStudentBodyPhotoFileNames(studentId, assessmentDate)
      : undefined;
    const legacyUndatedFolderFileNames = assessmentDate
      ? undefined
      : await listStudentBodyPhotoFileNames(studentId);
    const legacyPortalFolderFileNames = assessmentDate
      ? undefined
      : await listLegacyPortalBodyPhotoFileNames(studentId);
    const photos: StudentPhoto[] = [];

    for (const position of STUDENT_BODY_PHOTO_POSITIONS) {
      const resolvedPhoto = await resolveStudentBodyPhotoUrl(
        studentId,
        position,
        legacyUserId,
        assessmentDate,
        datedFolderFileNames,
        legacyUndatedFolderFileNames,
        legacyPortalFolderFileNames,
        options,
      );

      if (resolvedPhoto) {
        photos.push({
          id: photos.length + 1,
          student_id: studentId,
          position,
          url: resolvedPhoto.url,
          uploaded_at: resolvedPhoto.uploadedAt,
        });
      }
    }

    return photos;
  }

  async getBodyPhotos(
    studentId: number,
    legacyUserId?: string | null,
    options: GetBodyPhotosOptions = {},
  ): Promise<StudentPhoto[]> {
    try {
      const assessments = await StudentPhotoUploadService.listBodyPhotoAssessments({ studentId });
      const matched = options.assessmentDate
        ? assessments.find((item) => item.assessmentDate === options.assessmentDate)
        : assessments[0];

      if (!matched) {
        return [];
      }

      return matched.photos.map((photo, index) => ({
        id: index + 1,
        student_id: studentId,
        position: photo.position,
        url: photo.url,
        uploaded_at: photo.uploadedAt,
      }));
    } catch {
      return this.getBodyPhotosFromClientStorage(studentId, legacyUserId, options);
    }
  }

  async listBodyPhotoAssessments(
    studentId: number,
    legacyUserId?: string | null,
    options: GetBodyPhotosOptions = {},
  ): Promise<StudentBodyPhotoAssessment[]> {
    try {
      const assessments = await StudentPhotoUploadService.listBodyPhotoAssessments({ studentId });
      return assessments.map((assessment) => ({
        assessmentDate: assessment.assessmentDate,
        photos: assessment.photos.map((photo, index) => ({
          id: index + 1,
          student_id: studentId,
          position: photo.position,
          url: photo.url,
          uploaded_at: photo.uploadedAt,
        })),
      }));
    } catch {
      // Fallback para Storage direto quando a edge não estiver disponível.
    }

    const assessmentDates = await listStudentBodyPhotoAssessmentDates(studentId);

    if (assessmentDates.length > 0) {
      const assessments = await Promise.all(
        assessmentDates.map(async (assessmentDate) => ({
          assessmentDate,
          photos: await this.getBodyPhotosFromClientStorage(studentId, legacyUserId, {
            ...options,
            assessmentDate,
          }),
        })),
      );

      return assessments.filter((assessment) => assessment.photos.length > 0);
    }

    const latestPhotos = await this.getBodyPhotosFromClientStorage(studentId, legacyUserId, options);

    if (latestPhotos.length === 0) {
      return [];
    }

    const uploadedAt = latestPhotos.find((photo) => photo.uploaded_at)?.uploaded_at ?? '';

    return [{
      assessmentDate: uploadedAt ? uploadedAt.slice(0, 10) : 'legacy',
      photos: latestPhotos,
    }];
  }

  async getProfilePictureUrl(
    studentId: number,
    forceRefresh = false,
    legacyUserId?: string | null,
  ): Promise<string | null> {
    return resolveStudentProfilePhotoUrl(studentId, { legacyUserId, forceRefresh });
  }

  async uploadBodyPhoto(
    studentId: number,
    position: PhotoPosition,
    file: File,
    options: BodyPhotoUploadOptions = {},
  ): Promise<string> {
    try {
      return await StudentPhotoUploadService.uploadPhoto({
        studentId,
        photoType: 'body',
        position,
        file,
        studentToken: options.studentToken,
        assessmentDate: options.assessmentDate,
      });
    } catch (error) {
      handleServiceError(error, 'enviar foto do corpo');
      throw error;
    }
  }

  async uploadBodyPhotos(
    studentId: number,
    photos: BodyPhotoUploadItem[],
    options: BodyPhotoUploadOptions = {},
  ): Promise<Partial<Record<PhotoPosition, string>>> {
    if (photos.length === 0) {
      return {};
    }

    const uploads = await Promise.all(
      photos.map(async ({ position, file }) => {
        const url = await this.uploadBodyPhoto(studentId, position, file, options);
        return [position, url] as const;
      }),
    );

    return Object.fromEntries(uploads) as Partial<Record<PhotoPosition, string>>;
  }

  async deleteBodyPhoto(
    studentId: number,
    position: PhotoPosition,
    options: BodyPhotoUploadOptions = {},
  ): Promise<void> {
    try {
      await StudentPhotoUploadService.deletePhoto({
        studentId,
        photoType: 'body',
        position,
        studentToken: options.studentToken,
        assessmentDate: options.assessmentDate,
      });
    } catch (error) {
      handleServiceError(error, 'remover foto do corpo');
      throw error;
    }
  }

  async deleteBodyPhotos(
    studentId: number,
    positions: PhotoPosition[],
    options: BodyPhotoUploadOptions = {},
  ): Promise<void> {
    if (positions.length === 0) {
      return;
    }

    await Promise.all(
      positions.map((position) => this.deleteBodyPhoto(studentId, position, options)),
    );
  }

  async deleteLegacyUserBodyPhotos(
    legacyUserId: string,
    positions: PhotoPosition[],
  ): Promise<void> {
    if (positions.length === 0) {
      return;
    }

    const storagePaths = positions.flatMap((position) => (
      getStudentBodyPhotoFileNamesToResolve(position).map((fileName) => (
        buildLegacyStudentBodyPhotoPath(legacyUserId, fileName)
      ))
    ));

    const { error } = await supabase.storage
      .from(STUDENT_PHOTOS_BUCKET)
      .remove(storagePaths);

    if (error) {
      handleServiceError(error, 'remover fotos corporais legadas');
      throw error;
    }
  }

  async uploadProfilePicture(
    studentId: number,
    file: File,
    options: BodyPhotoUploadOptions = {},
  ): Promise<string> {
    try {
      const uploadedUrl = await StudentPhotoUploadService.uploadPhoto({
        studentId,
        photoType: 'profile',
        file,
        studentToken: options.studentToken,
      });

      return `${uploadedUrl}?t=${Date.now()}`;
    } catch (error) {
      handleServiceError(error, 'enviar foto de perfil');
      throw error;
    }
  }

  async getPortalUserProfilePictureUrl(userId: string, forceRefresh = false): Promise<string | null> {
    const path = buildPortalUserProfilePhotoPath(userId);
    const publicUrl = await resolveAvailableStudentPhotoUrl(path);

    if (!publicUrl) {
      return null;
    }

    return forceRefresh ? `${publicUrl}?t=${Date.now()}` : publicUrl;
  }

  async uploadPortalUserProfilePicture(userId: string, file: File): Promise<string> {
    const path = buildPortalUserProfilePhotoPath(userId);
    const { error } = await supabase.storage
      .from(STUDENT_PHOTOS_BUCKET)
      .upload(path, file, {
        upsert: true,
        contentType: file.type || 'image/jpeg',
      });

    if (error) handleServiceError(error, 'enviar foto de perfil');

    const { data: authData } = await supabase.auth.getUser();
    await insertStorageFileByPath({
      bucketId: STUDENT_PHOTOS_BUCKET,
      storagePath: path,
      contentType: file.type || 'image/jpeg',
      sizeBytes: file.size,
      ownerType: 'portal_user',
      ownerId: userId,
      fileKind: 'profile_photo',
      uploadedByUserId: authData.user?.id ?? null,
    });

    const uploadedUrl = await this.getPortalUserProfilePictureUrl(userId, true);

    if (uploadedUrl) {
      return uploadedUrl;
    }

    const signedUrl = await getStudentPhotoSignedUrl(path);

    if (!signedUrl) {
      handleServiceError(new Error('Não foi possível assinar a URL da foto de perfil.'), 'enviar foto de perfil');
    }

    return withPhotoCacheBuster(signedUrl!, true);
  }

  async listEquipmentPhotos(studentId: number): Promise<StudentEquipmentPhoto[]> {
    try {
      return await listStudentEquipmentPhotos(studentId);
    } catch (error) {
      handleServiceError(error, 'listar fotos de equipamentos');
      return [];
    }
  }

  async uploadEquipmentPhotos(
    studentId: number,
    files: File[],
    options: BodyPhotoUploadOptions = {},
  ): Promise<string[]> {
    try {
      return await StudentPhotoUploadService.uploadEquipmentPhotos({
        studentId,
        files,
        studentToken: options.studentToken,
      });
    } catch (error) {
      handleServiceError(error, 'enviar fotos de equipamentos');
      throw error;
    }
  }
}

export const PhotoService = new PhotoServiceClass();
